import { Link } from "react-router-dom";
import { Bed, Bath, MapPin, ExternalLink } from "lucide-react";
import { Card, CardContent } from "./ui/card";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";

const ZOOPLA_BRANCH_URL =
  "https://www.zoopla.co.uk/find-agents/branch/keystone-properties-batley-166551/";

const PropertyCard = ({ property }) => {
  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      maximumFractionDigits: 0,
    }).format(price);
  };

  return (
    <Card className="property-card overflow-hidden border-slate-200 hover:border-navy/20" data-testid={`property-card-${property.id}`}>
      <div className="img-zoom relative">
        <img
          src={property.images?.[0] || "https://images.unsplash.com/photo-1568605114967-8130f3a36994?w=800"}
          alt={property.title}
          className="w-full h-56 object-cover"
        />
        <Badge className="absolute top-4 left-4 bg-navy text-white">
          {property.property_type}
        </Badge>
        <a
          href={ZOOPLA_BRANCH_URL}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="absolute top-4 right-4 inline-flex items-center gap-1 rounded-full bg-[#4C2C79] hover:bg-[#3d2361] text-white px-2.5 py-1 text-[11px] font-semibold shadow-sm transition-colors z-10"
          data-testid={`property-zoopla-ribbon-${property.id}`}
          title="View this branch on Zoopla (opens in new tab)"
          aria-label="Also on Zoopla — view branch"
        >
          <span
            className="inline-flex items-center justify-center rounded bg-white text-[#4C2C79] px-1 py-px text-[9px] font-black tracking-wider leading-none"
            aria-hidden="true"
          >
            Zoopla
          </span>
          Also on Zoopla
          <ExternalLink className="w-3 h-3 opacity-80" aria-hidden="true" />
        </a>
        {!property.available && (
          <div className="absolute inset-0 bg-navy/60 flex items-center justify-center">
            <span className="text-white font-heading font-semibold text-lg">Let Agreed</span>
          </div>
        )}
      </div>
      <CardContent className="p-6">
        <div className="flex items-start justify-between mb-3">
          <h3 className="font-heading font-semibold text-lg text-navy line-clamp-2">
            {property.title}
          </h3>
        </div>
        
        <div className="flex items-center gap-1 text-slate-500 mb-4">
          <MapPin className="w-4 h-4" />
          <span className="text-sm">{property.location}</span>
        </div>

        <div className="flex items-center gap-4 mb-4">
          <div className="flex items-center gap-1 text-slate-600">
            <Bed className="w-4 h-4" />
            <span className="text-sm">{property.bedrooms} bed</span>
          </div>
          <div className="flex items-center gap-1 text-slate-600">
            <Bath className="w-4 h-4" />
            <span className="text-sm">{property.bathrooms} bath</span>
          </div>
        </div>

        <div className="flex items-center justify-between pt-4 border-t border-slate-100">
          <div>
            <span className="text-2xl font-heading font-semibold text-navy">
              {formatPrice(property.price)}
            </span>
            <span className="text-slate-500 text-sm"> /month</span>
          </div>
          <Button
            asChild
            variant="outline"
            className="border-navy text-navy hover:bg-navy hover:text-white"
            data-testid={`property-enquire-${property.id}`}
          >
            <Link to={`/properties/${property.id}`}>View Details</Link>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default PropertyCard;
